import axios from 'axios';
import { getAuthHeaders } from '../utils/authUtils';
import { ReturnCodes } from '../utils/constants';

class FileSystemAccessService {
    constructor() {
        // Set defaults for new users
        if (!localStorage.getItem('apiVersion')) {
            localStorage.setItem('apiVersion', 'v2');
        }
        if (!localStorage.getItem('videoExperience')) {
            localStorage.setItem('videoExperience', 'new');
        }
        if (!localStorage.getItem('userExperience')) {
            localStorage.setItem('userExperience', 'new');
        }
    }

    get apiVersion() {
        return localStorage.getItem('apiVersion') || 'v2';
    }

    getApiBaseUrl() {
        // Return the base URL based on the selected API version
        if (this.apiVersion === 'v2') {
            return `${process.env.REACT_APP_API_URL}/api/v2`;
        } else {
            return `${process.env.REACT_APP_API_URL}/api/v1`;
        }
    }

    // Method to switch API versions
    setApiVersion(version) {
        if (version === 'v1' || version === 'v2') {
            localStorage.setItem('apiVersion', version);

            // Dispatch event to notify UI components about the change
            const event = new CustomEvent('uiModeChanged', {
                detail: {
                    apiVersion: version,
                    uiMode: this.uiMode,
                },
            });
            window.dispatchEvent(event);

            return true;
        }
        return false;
    }

    getUiMode() {
        return this.uiMode;
    }

    async addVideo2Folder(subPath, videoURL, videoId, userData) {
        return new Promise(async (resolve, reject) => {
            try {
                await axios
                    .post(
                        `${this.getApiBaseUrl()}/filesystem/${subPath}`,
                        {
                            name: videoURL,
                            isFolder: false,
                            data: videoId,
                        },
                        {
                            headers: {
                                email: userData.email,
                                access: userData.access,
                                'Access-Control-Allow-Origin': '*',
                                'Access-Control-Allow-Credentials': true,
                            },
                        },
                    )
                    .then(function (response) {
                        resolve(ReturnCodes.FILE_UPLOAD_SUCCESS);
                    })
                    .catch(function (error) {
                        reject(ReturnCodes.FILE_UPLOAD_FAILED);
                    });
            } catch (error) {
                reject(ReturnCodes.FILE_UPLOAD_FAILED);
            }
        });
    }

    async createFolder(urls, fileName, email, access) {
        const headers = getAuthHeaders();
        console.log('createFolder', urls, fileName, email, access);
        return new Promise(async (resolve, reject) => {
            try {
                await axios
                    .post(
                        `${this.getApiBaseUrl()}/filesystem/${urls}`,
                        {
                            name: fileName,
                            isFolder: true,
                            data: '',
                        },
                        {
                            headers,
                        },
                    )
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } catch (error) {
                reject(error);
            }
        });
    }

    async delete(apisurls, email, access, force = false) {
        const forceParam = force ? '?force=true' : '';
        const authUserEmail = JSON.parse(localStorage.getItem('authUser')).email;
        const authToken = JSON.parse(localStorage.getItem('authToken'));
        try {
            const response = await axios.delete(`${this.getApiBaseUrl()}/filesystem${apisurls}${forceParam}`, {
                headers: {
                    email: authUserEmail,
                    access: authToken,
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Credentials': true,
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async renameFolder(apisurls, newName, email, access) {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.put(
                `${this.getApiBaseUrl()}/filesystem/rename${apisurls}`,
                {
                    name: newName,
                },
                {
                    headers,
                },
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async getFolderContents(apisurls, email, access) {
        try {
            const response = await axios.get(`${this.getApiBaseUrl()}/filesystem/${apisurls}`, {
                headers: getAuthHeaders(),
            });
            return response;
        } catch (error) {
            throw error;
        }
    }

    // Method to test API version availability
    async testApiVersion(version) {
        try {
            // Perform a simple health check
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/${version}/health`);
            return response.status === 200;
        } catch (error) {
            console.error(`API version ${version} is not available:`, error);
            return false;
        }
    }

    // Automatic fallback mechanism
    async useOptimalApiVersion() {
        try {
            // Try v2 first (MongoDB)
            const v2Available = await this.testApiVersion('v2');
            if (v2Available) {
                this.setApiVersion('v2');
                console.log('Using MongoDB API (v2)');
                return 'v2';
            } else {
                // Fall back to v1 (SQL)
                this.setApiVersion('v1');
                console.log('Using SQL API (v1)');
                return 'v1';
            }
        } catch (error) {
            // Default to v1 in case of any errors
            this.setApiVersion('v1');
            console.log('Error detecting API version, falling back to v1');
            return 'v1';
        }
    }

    async listFilesAndFolders(path, apiVersion = 'v1') {
        try {
            const headers = getAuthHeaders(true);
            // Normalize path for API request
            // Make API request
            const response = await axios.get(`${this.getApiBaseUrl()}/filesystem/all/${path}`, {
                headers,
            });

            return response.data;
        } catch (error) {
            console.error('Error listing files and folders:', error);
            throw error;
        }
    }

    /**
     * Move an item (file or folder) to a different location
     *
     * @param {Object} item - The item to move (with properties isFolder, path/id, etc.)
     * @param {string} sourcePath - Current path of the item
     * @param {string} destinationPath - Path to move the item to
     * @param {string} email - User's email (optional, will use from localStorage if not provided)
     * @param {string} access - User's access token (optional, will use from localStorage if not provided)
     * @returns {Promise<Object>} - Promise resolving to the API response
     */
    async moveItem(item, sourcePath, destinationPath, email, access) {
        try {
            // Get authentication data if not provided
            const headers = getAuthHeaders(true);

            const endpoint = `${this.getApiBaseUrl()}/filesystem/move`;

            const response = await axios.post(
                endpoint,
                {
                    sourcePath: item.isFolder ? item.path : sourcePath,
                    destinationPath: destinationPath,
                    isFolder: item.isFolder,
                    itemId: item.isFolder ? undefined : item.id,
                },
                {
                    headers,
                },
            );

            return response.data;
        } catch (error) {
            console.error('Error moving item:', error);
            throw error;
        }
    }

    async getFolderHistory(folderId) {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.get(`${this.getApiBaseUrl()}/filesystem/folder-history/${folderId}`, {
                headers,
            });

            return response.data;
        } catch (error) {
            console.error('Error fetching folder history:', error);
            throw error;
        }
    }

    // Add this method to the FileSystemAccessService class
    backfillFileTypes = async (type = 'video') => {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.post(
                `${this.getApiBaseUrl()}/filesystem/backfill/file-types`,
                { type },
                {
                    headers,
                },
            );
            return response.data;
        } catch (error) {
            console.error('Error backfilling file types:', error);
            throw error;
        }
    };
}

const fileSystemAccessServiceInstance = new FileSystemAccessService();
export default fileSystemAccessServiceInstance;
